import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  FileText, Shield, CheckCircle, XCircle, Clock, AlertTriangle,
  RefreshCw, Users, ChevronRight, Search, Filter, Activity,
  AlertCircle, Calendar, TrendingUp, BarChart3, GraduationCap,
  UserCheck, FileWarning
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// DOCUMENT INTELLIGENCE PAGE
// Section 9: Smart Document Validation + Onboarding Tracking
// ═══════════════════════════════════════════════════════════════

export default function DocumentIntelligencePage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState(null);
  const [onboardingDashboard, setOnboardingDashboard] = useState(null);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffValidation, setStaffValidation] = useState(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await api.get('/document-intelligence/dashboard');
      setDashboard(res.data.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      toast.error('Failed to load document intelligence dashboard');
    }
  }, []);

  // Load expiring documents
  const loadExpiring = useCallback(async () => {
    try {
      const res = await api.get('/document-intelligence/expiring?days=60');
      setExpiringDocs(res.data.data);
    } catch (err) {
      console.error('Failed to load expiring docs:', err);
    }
  }, []);

  // Load onboarding dashboard
  const loadOnboarding = useCallback(async () => {
    try {
      const res = await api.get('/document-intelligence/onboarding/dashboard');
      setOnboardingDashboard(res.data.data);
    } catch (err) {
      console.error('Failed to load onboarding dashboard:', err);
    }
  }, []);

  // Load NDIS rules
  const loadRules = useCallback(async () => {
    try {
      const res = await api.get('/document-intelligence/rules');
      setRules(res.data.data);
    } catch (err) {
      console.error('Failed to load rules:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadExpiring(), loadOnboarding(), loadRules()]);
      setLoading(false);
    };
    loadAll();
  }, [loadDashboard, loadExpiring, loadOnboarding, loadRules]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([loadDashboard(), loadExpiring(), loadOnboarding()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  const handleValidateStaff = async (staffId) => {
    if (!staffId) return;
    try {
      setSelectedStaffId(staffId);
      const res = await api.get(`/document-intelligence/staff/${staffId}/validate`);
      setStaffValidation(res.data.data);
      setValidationDialogOpen(true);
    } catch (err) {
      toast.error('Failed to validate staff documents');
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="loading-spinner">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-intelligence-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Document Intelligence</h1>
          <p className="text-muted-foreground">NDIS compliance validation &amp; onboarding tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="expiring" data-testid="tab-expiring">
            <FileWarning className="h-4 w-4 mr-2" />
            Expiring Docs
          </TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">
            <GraduationCap className="h-4 w-4 mr-2" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Shield className="h-4 w-4 mr-2" />
            NDIS Rules
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Compliance Rate"
              value={`${dashboard?.overview?.compliance_rate || 0}%`}
              icon={Shield}
              color={dashboard?.overview?.compliance_rate >= 80 ? 'text-emerald-600' : 'text-amber-600'}
              description={`${dashboard?.overview?.compliant || 0} of ${dashboard?.overview?.total_staff || 0} staff compliant`}
            />
            <StatCard
              title="Blocked from Roster"
              value={dashboard?.overview?.blocked_from_roster || 0}
              icon={XCircle}
              color="text-destructive"
              description="Staff with blocking issues"
            />
            <StatCard
              title="Expired Documents"
              value={dashboard?.document_issues?.total_expired || 0}
              icon={AlertCircle}
              color="text-destructive"
              description="Require immediate attention"
            />
            <StatCard
              title="Expiring Soon"
              value={dashboard?.document_issues?.total_expiring_soon || 0}
              icon={Clock}
              color="text-amber-600"
              description="Within 30 days"
            />
          </div>

          {/* Blocked Staff List */}
          {dashboard?.blocked_staff?.length > 0 && (
            <Card className="border-destructive/50">
              <CardHeader>
                <CardTitle className="text-base text-destructive flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  Blocked from Roster ({dashboard.blocked_staff.length})
                </CardTitle>
                <CardDescription>
                  These staff members cannot be assigned shifts due to document issues
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.blocked_staff.map((staff, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{staff.staff_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Issues: {staff.blocking_reasons.join(', ')}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleValidateStaff(staff.staff_id)}
                        data-testid={`validate-${staff.staff_id}`}
                      >
                        View Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expiring Soon Staff */}
          {dashboard?.expiring_soon_staff?.length > 0 && (
            <Card className="border-amber-500/50">
              <CardHeader>
                <CardTitle className="text-base text-amber-600 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Documents Expiring Soon ({dashboard.expiring_soon_staff.length})
                </CardTitle>
                <CardDescription>
                  Schedule renewals to maintain compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dashboard.expiring_soon_staff.map((staff, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-amber-500/5 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{staff.staff_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Expiring: {staff.expiring_documents.join(', ')}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleValidateStaff(staff.staff_id)}
                      >
                        View Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Expiring Documents Tab */}
        <TabsContent value="expiring" className="space-y-4">
          <ExpiringDocumentsPanel data={expiringDocs} onValidate={handleValidateStaff} />
        </TabsContent>

        {/* Onboarding Tab */}
        <TabsContent value="onboarding" className="space-y-4">
          <OnboardingPanel data={onboardingDashboard} onRefresh={loadOnboarding} />
        </TabsContent>

        {/* NDIS Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <NDISRulesPanel data={rules} />
        </TabsContent>
      </Tabs>

      {/* Staff Validation Dialog */}
      <StaffValidationDialog
        open={validationDialogOpen}
        onOpenChange={setValidationDialogOpen}
        data={staffValidation}
        staffId={selectedStaffId}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function StatCard({ title, value, icon: Icon, color, description }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold ${color}`}>{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}

function ExpiringDocumentsPanel({ data, onValidate }) {
  if (!data) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  const { by_urgency } = data;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-destructive">Critical (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{by_urgency?.critical?.count || 0}</div>
          </CardContent>
        </Card>
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-600">Warning (8-30 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{by_urgency?.warning?.count || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Upcoming (31-60 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{by_urgency?.upcoming?.count || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Critical Items */}
      {by_urgency?.critical?.items?.length > 0 && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Critical - Expires within 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Days Left</TableHead>
                  <TableHead>Blocks Roster</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {by_urgency.critical.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <button 
                        className="font-medium text-sm text-primary hover:underline"
                        onClick={() => onValidate(item.staff_id)}
                      >
                        {item.staff_name}
                      </button>
                    </TableCell>
                    <TableCell>{item.display_name}</TableCell>
                    <TableCell>{item.expiry_date?.split('T')[0]}</TableCell>
                    <TableCell>
                      <Badge variant="destructive">{item.days_until_expiry} days</Badge>
                    </TableCell>
                    <TableCell>
                      {item.blocks_roster ? (
                        <Badge variant="destructive">Yes</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Warning Items */}
      {by_urgency?.warning?.items?.length > 0 && (
        <Card className="border-amber-500/50">
          <CardHeader>
            <CardTitle className="text-base text-amber-600">Warning - Expires within 30 days</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Days Left</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {by_urgency.warning.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <button 
                        className="font-medium text-sm text-primary hover:underline"
                        onClick={() => onValidate(item.staff_id)}
                      >
                        {item.staff_name}
                      </button>
                    </TableCell>
                    <TableCell>{item.display_name}</TableCell>
                    <TableCell>{item.expiry_date?.split('T')[0]}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-amber-600 border-amber-500">
                        {item.days_until_expiry} days
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {data.total_expiring === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-medium">No Documents Expiring Soon</p>
            <p className="text-muted-foreground">All documents are up to date</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function OnboardingPanel({ data, onRefresh }) {
  if (!data) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Workflows"
          value={data.overview?.total_workflows || 0}
          icon={Users}
          color="text-primary"
          description="Staff onboarding tracked"
        />
        <StatCard
          title="In Progress"
          value={data.overview?.in_progress || 0}
          icon={Activity}
          color="text-amber-600"
          description="Active onboardings"
        />
        <StatCard
          title="Completed"
          value={data.overview?.completed || 0}
          icon={CheckCircle}
          color="text-emerald-600"
          description="Fully onboarded"
        />
        <StatCard
          title="Overdue Tasks"
          value={data.overview?.overdue_tasks || 0}
          icon={AlertTriangle}
          color={data.overview?.overdue_tasks > 0 ? 'text-destructive' : 'text-muted-foreground'}
          description="Need attention"
        />
      </div>

      {/* Stage Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Onboarding by Stage</CardTitle>
          <CardDescription>Staff distribution across onboarding stages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.stages?.map((stage) => {
              const count = data.by_stage?.[stage.key] || 0;
              const total = data.overview?.total_workflows || 1;
              const percentage = Math.round((count / total) * 100);
              
              return (
                <div key={stage.key} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Badge variant={stage.key === 'ACTIVE' ? 'default' : 'outline'} className="text-xs">
                        {stage.order}
                      </Badge>
                      {stage.name}
                    </span>
                    <span className="font-medium">{count}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Active Onboardings */}
      {data.active_onboardings?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active Onboardings</CardTitle>
            <CardDescription>Staff currently being onboarded</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Current Stage</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.active_onboardings.map((ob, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{ob.staff_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{ob.stage_name}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ob.updated_at ? new Date(ob.updated_at).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function NDISRulesPanel({ data }) {
  if (!data) {
    return <div className="text-center py-8 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Mandatory Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            NDIS Mandatory Documents
          </CardTitle>
          <CardDescription>
            Required for all support workers under NDIS Practice Standards
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.mandatory_documents?.map((doc) => (
              <Badge key={doc} variant="default" className="text-xs">
                {doc.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* All Rules Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document Validation Rules</CardTitle>
          <CardDescription>
            NDIS-specific requirements and validity periods
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Validity</TableHead>
                <TableHead>Critical</TableHead>
                <TableHead>Blocks Roster</TableHead>
                <TableHead>Renewal Warning</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rules?.map((rule) => (
                <TableRow key={rule.document_type}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{rule.display_name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1" title={rule.ndis_requirement}>
                        {rule.ndis_requirement}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {rule.max_validity_years ? `${rule.max_validity_years} years` : 'Variable'}
                  </TableCell>
                  <TableCell>
                    {rule.is_critical ? (
                      <Badge variant="destructive">Yes</Badge>
                    ) : (
                      <Badge variant="secondary">No</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {rule.blocks_roster ? (
                      <Badge variant="destructive">Yes</Badge>
                    ) : (
                      <Badge variant="secondary">No</Badge>
                    )}
                  </TableCell>
                  <TableCell>{rule.renewal_warning_days} days</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Role-Specific Requirements</CardTitle>
          <CardDescription>
            Additional documents required by staff role
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(data.role_requirements || {}).map(([role, docs]) => (
              <div key={role} className="border-b pb-3 last:border-0">
                <p className="font-medium text-sm mb-2">{role.replace(/_/g, ' ')}</p>
                <div className="flex flex-wrap gap-1">
                  {docs.map((doc) => (
                    <Badge key={doc} variant="outline" className="text-xs">
                      {doc.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StaffValidationDialog({ open, onOpenChange, data, staffId }) {
  if (!data) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'VALID': return 'text-emerald-600 bg-emerald-100';
      case 'EXPIRED': return 'text-destructive bg-destructive/10';
      case 'EXPIRING_SOON': return 'text-amber-600 bg-amber-100';
      case 'MISSING': return 'text-gray-600 bg-gray-100';
      case 'INVALID': return 'text-destructive bg-destructive/10';
      case 'PENDING_VERIFICATION': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Document Validation - {data.staff_name}</DialogTitle>
          <DialogDescription>
            NDIS compliance status for all required documents
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 py-4">
          <div className={`p-4 rounded-lg ${data.can_work ? 'bg-emerald-50' : 'bg-destructive/10'}`}>
            <p className="text-sm text-muted-foreground">Can Work</p>
            <p className={`text-2xl font-bold ${data.can_work ? 'text-emerald-600' : 'text-destructive'}`}>
              {data.can_work ? 'Yes' : 'No'}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-primary/10">
            <p className="text-sm text-muted-foreground">Compliance Score</p>
            <p className="text-2xl font-bold text-primary">{data.document_compliance_score}%</p>
          </div>
          <div className="p-4 rounded-lg bg-amber-50">
            <p className="text-sm text-muted-foreground">Issues</p>
            <p className="text-2xl font-bold text-amber-600">
              {(data.blocking_documents?.length || 0) + (data.missing_mandatory?.length || 0)}
            </p>
          </div>
        </div>

        {/* Blocking Documents Alert */}
        {data.blocking_documents?.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Blocked from roster:</strong> {data.blocking_documents.join(', ')}
            </AlertDescription>
          </Alert>
        )}

        {/* Validation Results */}
        <div className="space-y-2">
          {data.validation_results?.map((result, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{result.display_name}</p>
                  <Badge className={`text-xs ${getStatusColor(result.status)}`}>
                    {result.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                {result.action_required && (
                  <p className="text-xs text-muted-foreground mt-1">{result.action_required}</p>
                )}
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {result.days_until_expiry !== null && (
                  <span className={result.days_until_expiry <= 30 ? 'text-amber-600 font-medium' : ''}>
                    {result.days_until_expiry >= 0 ? `${result.days_until_expiry} days left` : `Expired ${Math.abs(result.days_until_expiry)} days ago`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
